<template>
  <v-container fluid class="admin-workflows">
    <NcrmHomePageHeader
      icon="mdi-shield-crown-outline"
      :title="$t('admin.workflows.title')"
      :subtitle="$t('admin.workflows.subtitle')"
    >
      <v-btn color="primary" variant="tonal" :loading="loading" @click="loadWorkspace">
        <v-icon start>mdi-refresh</v-icon>
        {{ $t("app.actions.refresh") }}
      </v-btn>
    </NcrmHomePageHeader>

    <v-alert v-if="errorMessage" type="error" class="mt-4">
      {{ errorMessage }}
    </v-alert>

    <NcrmHomeKpiCards class="mt-4" :items="kpiItems" />

    <v-card variant="flat" class="admin-workflows__panel mt-4">
      <div class="admin-workflows__panel-header">
        <div>
          <h2>{{ $t("admin.workflows.list.title") }}</h2>
          <p>{{ $t("admin.workflows.list.subtitle") }}</p>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="workflowRows"
        :loading="loading"
        item-key="key"
        density="comfortable"
        hide-default-footer
        :items-per-page="-1"
        :row-props="workflowRowProps"
        @click:row="handleWorkflowClick"
      >
        <template #item.name="{ item }">
          <div class="admin-workflows__name-cell">
            <v-icon :color="item.color" size="20">{{ item.icon }}</v-icon>
            <div class="min-w-0">
              <div class="admin-workflows__workflow-name">{{ item.name }}</div>
              <div class="admin-workflows__workflow-description">{{ item.description }}</div>
            </div>
          </div>
        </template>

        <template #item.status="{ item }">
          <v-chip :color="item.activeCount ? 'success' : 'secondary'">
            {{ item.activeCount ? $t("admin.workflows.statuses.active") : $t("admin.workflows.statuses.draft") }}
          </v-chip>
        </template>

        <template #item.action>
          <v-icon class="admin-workflows__row-chevron" size="18">mdi-chevron-right</v-icon>
        </template>

        <template #no-data>
          <v-alert type="info" class="ma-4">
            {{ $t("admin.workflows.empty.body") }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import { formatExternalApiDateTime } from "@/Utile/ncrmExternalApi.mjs";
import {
  ADMIN_WORKFLOW_DEFINITIONS,
  adminWorkflowRouteFor,
} from "./workflows/adminWorkflowDefinitions.mjs";
import { fetchAdminWorkflowInstances } from "./workflows/adminWorkflowsApi.mjs";

export default {
  name: "AdminWorkflowsHome",
  components: {
    NcrmHomeKpiCards,
    NcrmHomePageHeader,
  },
  data() {
    return {
      loading: false,
      errorMessage: "",
      workflows: [],
      templates: [],
      summaries: {},
    };
  },
  computed: {
    kpiItems() {
      return [
        { key: "workflowTypes", label: this.$t("admin.workflows.metrics.workflowTypes"), value: ADMIN_WORKFLOW_DEFINITIONS.length, icon: "mdi-sitemap-outline", color: "primary" },
        { key: "configured", label: this.$t("admin.workflows.metrics.configured"), value: this.workflows.length, icon: "mdi-cog-outline", color: "info" },
        { key: "active", label: this.$t("admin.workflows.metrics.active"), value: this.workflows.filter((workflow) => workflow.status === "active").length, icon: "mdi-play-circle-outline", color: "success" },
        { key: "admin", label: this.$t("admin.workflows.metrics.access"), value: this.$t("admin.workflows.metrics.adminOnly"), icon: "mdi-shield-crown-outline", color: "secondary" },
      ];
    },
    headers() {
      return [
        { title: this.$t("admin.workflows.table.workflow"), key: "name", sortable: false },
        { title: this.$t("admin.workflows.table.status"), key: "status", sortable: false },
        { title: this.$t("admin.workflows.table.instances"), key: "instances", sortable: false },
        { title: this.$t("admin.workflows.table.processed"), key: "mirrored", sortable: false },
        { title: this.$t("admin.workflows.table.lastRun"), key: "lastRun", sortable: false },
        { title: "", key: "action", sortable: false, width: 48 },
      ];
    },
    workflowRows() {
      return ADMIN_WORKFLOW_DEFINITIONS.map((definition) => {
        const base = {
          ...definition,
          name: this.$t(definition.titleKey),
          description: this.$t(definition.subtitleKey),
          route: adminWorkflowRouteFor(definition.key),
        };
        if (definition.summaryKind === "summary") {
          return { ...base, ...this.summaryRow(definition.key) };
        }
        const instances = this.workflows.filter((workflow) =>
          (workflow.workflowKey || workflow.workflow_key) === definition.key
        );
        const lastRunWorkflow = instances.find((workflow) => workflow.lastRunAt || workflow.last_run_at);
        return {
          ...base,
          instances: instances.length,
          activeCount: instances.filter((workflow) => workflow.status === "active").length,
          mirrored: instances.reduce((total, workflow) => total + Number(workflow.mirroredEventsCount || workflow.mirrored_events_count || 0), 0),
          lastRun: this.formatDate(lastRunWorkflow?.lastRunAt || lastRunWorkflow?.last_run_at),
        };
      });
    },
  },
  mounted() {
    this.loadWorkspace();
  },
  methods: {
    async loadWorkspace() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const { data } = await fetchAdminWorkflowInstances();
        this.workflows = Array.isArray(data?.workflows) ? data.workflows : [];
        this.templates = Array.isArray(data?.templates) ? data.templates : [];
        this.summaries = data?.summaries && typeof data.summaries === "object" ? data.summaries : {};
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.workflows.errors.load");
      } finally {
        this.loading = false;
      }
    },
    // Singleton workflows (no instance rows) read their operational counters
    // from the `summaries` block returned by the same listing call.
    summaryRow(workflowKey) {
      const summary = this.summaries?.[workflowKey] || null;
      const counts = summary?.counts || {};
      return {
        instances: summary?.configured ? 1 : 0,
        activeCount: summary?.dispatchActive ? 1 : 0,
        mirrored: Number(counts.succeeded || 0),
        lastRun: this.formatDate(counts.lastDispatchedAt),
      };
    },
    workflowRowProps() {
      return {
        class: "admin-workflows__row",
        style: "cursor: pointer;",
        tabindex: 0,
      };
    },
    handleWorkflowClick(_, row) {
      const workflow = row?.item || null;
      if (!workflow?.key) return;
      this.$router.push({ name: "AdminWorkflowDetails", params: { workflowKey: workflow.key } });
    },
    formatDate(value) {
      const locale = this.$i18n?.locale === "en" ? "en-US" : this.$i18n?.locale === "ru" ? "ru-RU" : "ro-RO";
      return formatExternalApiDateTime(value, locale);
    },
  },
};
</script>

<style scoped>
.admin-workflows {
  background: var(--app-page-background);
  min-height: 100%;
}

.admin-workflows__panel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
  padding: 20px;
}

.admin-workflows__panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.admin-workflows__panel-header h2 {
  margin: 0;
  color: var(--app-text-strong);
  font-size: 1.05rem;
  font-weight: 700;
}

.admin-workflows__panel-header p {
  margin: 4px 0 0;
  color: var(--app-text-default);
  opacity: 0.68;
  line-height: 1.5;
}

.admin-workflows__name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.admin-workflows__workflow-name {
  color: var(--app-text-strong);
  font-weight: 700;
}

.admin-workflows__workflow-description {
  color: var(--app-text-default);
  font-size: 0.82rem;
  opacity: 0.62;
}

.admin-workflows__row-chevron {
  opacity: 0.48;
}
</style>
